import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Check, Compass, Lightbulb, Target, TrendingUp, ArrowRight, 
  ArrowUpRight, Award, CheckCircle2, MessageSquare, 
  Users, Settings, Search, BrainCircuit, 
  ChevronRight, PlusCircle, MinusCircle, Clock, ExternalLink,
  FileText, BarChart, Shield, BookOpen, Zap,
  Hexagon, ArrowDown, Layers, LineChart, AlignLeft,
  Download, PenTool, Cpu, Rocket, Star,
  Box, ChevronDown, BarChart3, Share2, Lock,
  Workflow, Gauge, GitBranch, Sparkles, Network, Globe
} from 'lucide-react';

// Import common components
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';
import Button from './Button';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

// --- Spacing System ---
const SPACING = {
  xs: 'px-2 py-1',
  sm: 'px-4 py-2',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-12 md:py-16 lg:py-20',
  sectionMedium: 'py-10 md:py-14 lg:py-16',
  sectionCompact: 'py-8 md:py-10 lg:py-12',
  container: 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'
};

// Enhanced SEO Schema Creator
const createSchemaData = () => {
  // Base schema with more detailed structured data
  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Strategic Innovation Services | Innovation Consulting | Lusidea",
    "description": "Lusidea provides comprehensive innovation services including Future Thinking and Innovation Management to help organizations create sustainable competitive advantage and transform their business.",
    "url": "https://www.lusidea.com/services",
    "keywords": "innovation services, future thinking, innovation management, strategic innovation, innovation consulting, business transformation, competitive advantage, innovation strategy",
    "mainEntity": {
      "@type": "Service",
      "provider": {
        "@type": "Organization",
        "name": "Lusidea",
        "url": "https://www.lusidea.com",
        "logo": "https://www.lusidea.com/images/logo.png",
        "sameAs": [
          "https://www.linkedin.com/company/lusidea",
          "https://twitter.com/lusidea",
          "https://www.facebook.com/lusidea",
          "https://www.instagram.com/lusidea_innovation"
        ]
      },
      "name": "Innovation Consulting Services",
      "description": "Comprehensive innovation services to help organizations identify future opportunities and implement effective innovation systems",
      "offers": {
        "@type": "Offer",
        "itemOffered": [
          {
            "@type": "Service",
            "name": "Future Thinking",
            "description": "Anticipate market shifts and identify emerging opportunities before they become obvious to competitors."
          },
          {
            "@type": "Service",
            "name": "Innovation Management",
            "description": "Structure and optimize your innovation approach for greater efficiency, rapid implementation, and measurable results."
          }
        ]
      }
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", ".hero-description"]
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.lusidea.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://www.lusidea.com/services"
        }
      ]
    }
  };

  // Add FAQs to schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do your innovation services work together?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our Future Thinking and Innovation Management services complement each other perfectly in a complete innovation system. Future Thinking helps you see around corners to identify emerging opportunities and threats, while Innovation Management provides the structure to efficiently evaluate, select, and implement the right initiatives."
        }
      },
      {
        "@type": "Question",
        "name": "What's the typical timeline for seeing results?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our approach delivers value at multiple horizons: Short-term wins (2-3 months) through process improvements, Medium-term results (4-8 months) from new opportunity identification, and Long-term transformation (9+ months) through systematic innovation management."
        }
      },
      {
        "@type": "Question",
        "name": "How do you ensure innovation initiatives align with our business strategy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Strategic alignment is foundational to our methodology. We start by deeply understanding your organization's strategic objectives, market position, and growth ambitions."
        }
      }
    ]
  };

  // Add organization information
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Lusidea",
    "url": "https://www.lusidea.com",
    "logo": "https://www.lusidea.com/images/logo.png",
    "description": "Lusidea is a strategic innovation consultancy helping organizations create sustainable competitive advantage through Future Thinking and Innovation Management.",
    "sameAs": [
      "https://www.linkedin.com/company/lusidea",
      "https://twitter.com/lusidea",
      "https://www.facebook.com/lusidea",
      "https://www.instagram.com/lusidea_innovation"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-800-INNOVATE",
      "contactType": "customer service",
      "availableLanguage": ["English", "Spanish", "French"]
    }
  };

  return [baseSchema, faqSchema, organizationSchema];
};

// --- Main Component ---
const LusideaServicesPage = () => {
  // Generate structured data for SEO
  const structuredData = useMemo(() => createSchemaData(), []);

  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      {/* SEO Metadata - Enhanced with React Helmet */}
      <Helmet>
        <title>Innovation Framework | Strategic Innovation System | Lusidea</title>
        <meta name="description" content="Lusidea builds powerful innovation ecosystems through Future Thinking and Innovation Management. Create sustainable strategic advantage and measurable business impact." />
        <meta name="keywords" content="innovation engine, innovation ecosystem, innovation framework, future thinking, innovation management, strategic innovation, innovation consulting, business transformation, competitive advantage, innovation strategy" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.lusidea.com/services" />
        
        {/* Open Graph Tags - Enhanced */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lusidea.com/services" />
        <meta property="og:title" content="Innovation Framework | Strategic Innovation System | Lusidea" />
        <meta property="og:description" content="Transform your organization with Lusidea's innovation methodology. Create sustainable competitive advantage through our proven innovation ecosystem." />
        <meta property="og:image" content="https://www.lusidea.com/images/services-og-image.jpg" />
        <meta property="og:image:alt" content="Lusidea innovation framework showing the transformation from strategic vision to business results" />
        <meta property="og:site_name" content="Lusidea" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Tags - Enhanced */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@lusidea" />
        <meta name="twitter:creator" content="@lusidea" />
        <meta name="twitter:title" content="Innovation Framework | Lusidea" />
        <meta name="twitter:description" content="Transform your organization with Lusidea's proven innovation ecosystem. Future Thinking and Innovation Management methodologies for sustainable growth." />
        <meta name="twitter:image" content="https://www.lusidea.com/images/services-twitter-card.jpg" />
        <meta name="twitter:image:alt" content="Lusidea's strategic innovation framework" />
        
        {/* Structured Data */}
        {structuredData.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
    
      <HeaderNav />
      
      <main id="main-content">
        <HeroSection />
        <ServiceDetailsSection />
        <IntegratedMethodologySection />
        <InnovationOutcomesSection />
        <ServicesFAQSection />
        <CTASection />
      </main>
      
      <FooterLan />
    </div>
  );
};

// --- Hero Section (Enhanced for better SEO and mobile responsiveness) ---
const HeroSection = memo(() => {
  return (
    <section className="relative pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20 overflow-hidden" aria-labelledby="hero-heading">
      {/* Dynamic background with better accessibility */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primaryLight via-white to-white">
        <div className="absolute inset-0 opacity-10" 
          style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0l30 17.32v34.64L30 60 0 51.96V17.32L30 0zm0 11.54l-18.46 10.65v21.3L30 54.14l18.46-10.65v-21.3L30 11.54z\' fill=\'%23ce2223\' fill-opacity=\'0.4\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            backgroundSize: '40px 40px'
          }} 
          aria-hidden="true"
        />
      </div>
      
      <div className={SPACING.container}>
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          {/* Left column - Text content */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm mb-4">
              <span className="w-2 h-2 rounded-full bg-primary"></span>
              <span className="text-sm font-medium text-darkGray">Strategic Innovation</span>
              <span className="w-2 h-2 rounded-full bg-primary"></span>
            </div>
            
            <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-black leading-tight mb-4">
              Transform with <span className="text-primary">Innovation Engine</span>
            </h1>
            
            <p className="hero-description text-base sm:text-lg text-darkGray mb-6 max-w-xl mx-auto lg:mx-0">
              Our integrated innovation framework creates sustainable competitive advantage by turning future insights into measurable business results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button href="#services" variant="primary" size="lg" icon={ArrowRight} aria-label="Explore Our Services" className="w-full sm:w-auto">
                Explore Our Services
              </Button>
              <Button href="#methodology" variant="outlineGray" size="lg" icon={Compass} aria-label="View Our Methodology" className="w-full sm:w-auto mt-3 sm:mt-0">
                View Our Methodology
              </Button>
            </div>
            
            {/* Service principles - Enhanced for mobile */}
            <div className="mt-8 pt-6 border-t border-black/10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: BrainCircuit, label: "Future-Focused", description: "Anticipate market changes" },
                { icon: Workflow, label: "Systematic", description: "Proven innovation ecosystem" },
                { icon: CheckCircle2, label: "Results-Driven", description: "Measurable business impact" }
              ].map((item, i) => (
                <div key={i} className="flex flex-row sm:flex-col items-center sm:items-start gap-3 sm:gap-0">
                  <div className="w-10 h-10 rounded-full bg-primaryLight/70 flex items-center justify-center sm:mb-2" aria-hidden="true">
                    {React.createElement(item.icon, { size: 20, className: 'text-primary' })}
                  </div>
                  <div className="flex flex-col sm:items-start">
                    <span className="font-medium text-black">{item.label}</span>
                    <span className="text-xs sm:text-sm text-mediumGray">{item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right column - Enhanced visual element with better accessibility and mobile optimization */}
          <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
            <div className="relative max-w-sm lg:max-w-lg">
              {/* Main image with better alt text and loading attributes */}
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="/img/innoprocess.png" 
                  alt="Visualization of Lusidea's strategic innovation framework connecting future thinking with innovation management" 
                  className="w-full h-auto"
                  width="600"
                  height="400"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
              
              {/* Floating elements - Enhanced for design coherence */}
              <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-md p-3 max-w-[180px] transform rotate-2 sm:rotate-3">
                <div className="flex items-center gap-2">
                  <LineChart size={20} className="text-primary flex-shrink-0" aria-hidden="true" />
                  <div>
                    <div className="text-xs font-medium text-mediumGray">Proven</div>
                    <div className="text-base font-bold text-black">95% Success Rate</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-3 -left-3 bg-primary/90 rounded-lg shadow-md py-2 px-3 transform -rotate-1 hidden sm:flex">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-white" aria-hidden="true" />
                  <span className="font-bold text-white text-sm">End-to-End Process</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// --- Service Details Section (Refined for responsive design) ---
const ServiceDetailsSection = memo(() => {
  return (
    <section id="services" className={`${SPACING.section} bg-white`} aria-labelledby="services-heading">
      <div className={SPACING.container}>
        {/* Section header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-3">
            <Settings size={18} className="text-primary" aria-hidden="true" />
          </div>
          <h2 id="services-heading" className="text-2xl md:text-3xl font-bold text-black mb-3">
            The Strategic Innovation Framework
          </h2>
          <p className="text-base md:text-lg text-darkGray/80 max-w-2xl mx-auto">
            Two specialized services working together to create a sustainable innovation ecosystem
          </p>
        </div>

        {/* Service Cards - Modern, responsive design */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-10">
          {/* Future Thinking Service Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-lightGray hover:shadow-md transition-all duration-300">
            <div className="p-5 md:p-6 pb-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center" aria-hidden="true">
                  <Compass size={20} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-black">Future Thinking</h3>
              </div>
              
              <p className="text-darkGray/90 mb-4 text-sm md:text-base">
                Our systematic approach to anticipating market shifts and identifying emerging opportunities to fuel your innovation pipeline.
              </p>
              
              <div className="mb-5">
                <h4 className="text-base font-semibold text-black mb-2">Key Methodologies</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "Trend monitoring and analysis",
                    "Signal scanning and interpretation",
                    "Scenario planning and development",
                    "Technology scouting and assessment",
                    "Consumer research and insights",
                    "Opportunity mapping and prioritization"
                  ].map((method, i) => (
                    <div key={i} className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-0.5 mr-2" aria-hidden="true">
                        <Check size={10} className="text-primary" />
                      </div>
                      <span className="text-xs md:text-sm text-darkGray/80">{method}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1.5">
                  <Target size={16} className="text-primary" aria-hidden="true" />
                  <h4 className="font-semibold text-black text-sm">Why Future Thinking Powers Your Innovation Strategy</h4>
                </div>
                <p className="text-xs text-darkGray/80">
                  In rapidly changing markets, organizations that can anticipate shifts gain first-mover advantage. Our Future Thinking methodology ensures your innovation ecosystem stays ahead of market changes and continuously identifies high-potential opportunities.
                </p>
              </div>
            </div>
            
            {/* Business outcomes section */}
            <div className="bg-gray-50 border-t border-lightGray p-4 md:p-5">
              <h4 className="text-sm font-semibold text-black mb-2 flex items-center">
                <TrendingUp size={14} className="text-primary mr-2" aria-hidden="true" />
                Business Outcomes
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "Identify emerging opportunities",
                  "Anticipate disruptive threats",
                  "Target R&D investments",
                  "Strategic risk reduction"
                ].map((outcome, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-4 h-4 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-0.5 mr-1.5" aria-hidden="true">
                      <Check size={8} className="text-primary" />
                    </div>
                    <span className="text-xs text-darkGray/80">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Innovation Management Service Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-lightGray hover:shadow-md transition-all duration-300">
            <div className="p-5 md:p-6 pb-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center" aria-hidden="true">
                  <Settings size={20} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-black">Innovation Management</h3>
              </div>
              
              <p className="text-darkGray/90 mb-4 text-sm md:text-base">
                Our structured approach to building and optimizing your innovation capabilities through effective governance, metrics, and implementation processes.
              </p>
              
              <div className="mb-5">
                <h4 className="text-base font-semibold text-black mb-2">Key Methodologies</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "Innovation strategy development",
                    "Portfolio management frameworks",
                    "Governance and evaluation systems",
                    "Implementation planning and tracking",
                    "Performance metrics and dashboards",
                    "Team capability development"
                  ].map((method, i) => (
                    <div key={i} className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-0.5 mr-2" aria-hidden="true">
                        <Check size={10} className="text-primary" />
                      </div>
                      <span className="text-xs md:text-sm text-darkGray/80">{method}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1.5">
                  <Settings size={16} className="text-primary" aria-hidden="true" />
                  <h4 className="font-semibold text-black text-sm">Why Innovation Management Strengthens Your Ecosystem</h4>
                </div>
                <p className="text-xs text-darkGray/80">
                  Even the best ideas fail without proper execution. Our Innovation Management approach provides the governance structures, evaluation frameworks, and implementation processes needed to transform your innovation system and deliver measurable results.
                </p>
              </div>
            </div>
            
            {/* Business outcomes section */}
            <div className="bg-gray-50 border-t border-lightGray p-4 md:p-5">
              <h4 className="text-sm font-semibold text-black mb-2 flex items-center">
                <TrendingUp size={14} className="text-primary mr-2" aria-hidden="true" />
                Business Outcomes
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "Higher implementation success",
                  "Faster time-to-market",
                  "Efficient resource allocation",
                  "Sustained innovation capability"
                ].map((outcome, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-4 h-4 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-0.5 mr-1.5" aria-hidden="true">
                      <Check size={8} className="text-primary" />
                    </div>
                    <span className="text-xs text-darkGray/80">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Integration message - Mobile optimized */}
        <div className="bg-primary/5 rounded-xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="md:w-1/4 relative h-24 sm:h-28 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-md z-10 border border-lightGray">
                <div className="text-primary font-bold text-lg">1+1=3</div>
              </div>
              <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center" aria-hidden="true">
                <div className="w-full border-2 border-dashed border-primary/20 rounded-full h-full"></div>
              </div>
            </div>
            <div className="md:w-3/4">
              <h3 className="text-lg font-bold text-black mb-2 text-center md:text-left">Creating a Complete Innovation Ecosystem</h3>
              <p className="text-sm text-darkGray/90 mb-3 text-center md:text-left">
                While each service delivers substantial value independently, their true power comes from their integration. Future Thinking feeds your innovation pipeline with high-potential opportunities, while Innovation Management provides the framework to efficiently transform those opportunities into business results.
              </p>
              <div className="flex justify-center md:justify-start">
                <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                  <Award size={14} className="text-primary" aria-hidden="true" />
                  <span className="text-xs font-medium">Proven methodology with 95% success rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// --- Integrated Methodology Section (Mobile optimized) ---
const IntegratedMethodologySection = memo(() => {
  return (
    <section id="methodology" className={`${SPACING.section} bg-gray-50`} aria-labelledby="methodology-heading">
      <div className={SPACING.container}>
        {/* Section header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-3">
            <GitBranch size={18} className="text-primary" aria-hidden="true" />
          </div>
          <h2 id="methodology-heading" className="text-2xl md:text-3xl font-bold text-black mb-3">
            The Innovation System Methodology
          </h2>
          <p className="text-base text-darkGray/80 max-w-2xl mx-auto">
            A comprehensive approach that drives transformation by connecting strategic foresight with structured execution
          </p>
        </div>

        {/* Methodology Visualization - Mobile optimized */}
        <div className="max-w-4xl mx-auto">
          {/* Central innovation core */}
          <div className="relative z-10 mb-10 md:mb-14">
            <div className="max-w-xs mx-auto bg-white rounded-xl shadow-sm p-5 text-center relative z-20">
              <div className="w-14 h-14 mx-auto mb-2 bg-primary/10 rounded-full flex items-center justify-center" aria-hidden="true">
                <Hexagon size={24} className="text-primary" />
              </div>
              <h3 className="text-lg font-bold text-black mb-1.5">Innovation Core</h3>
              <p className="text-xs md:text-sm text-darkGray/80">
                Our methodology integrates future insights with structured execution to power your innovation ecosystem
              </p>
            </div>
            
            {/* Connection lines - visible only on desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-primary/20 transform -translate-y-1/2 z-10" aria-hidden="true"></div>
            <div className="hidden md:block absolute top-32 bottom-0 left-1/2 w-px bg-primary/20 transform -translate-x-1/2 z-10" aria-hidden="true"></div>
          </div>
          
          {/* Methodology quadrants - Stacked on mobile, 2-column on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {/* Future Thinking quadrants */}
            <div className="space-y-5">
              <div className="text-center mb-2">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-2">
                  <Compass size={18} className="text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-black">Future Thinking</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  {
                    title: "Strategic Foresight",
                    icon: Target,
                    description: "Identify emerging trends and patterns to anticipate market shifts"
                  },
                  {
                    title: "Future Scenarios",
                    icon: Lightbulb, 
                    description: "Develop plausible future scenarios to guide strategic choices"
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-lightGray">
                    <div className="flex gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
                        {React.createElement(item.icon, { size: 16, className: 'text-primary' })}
                      </div>
                      <div>
                        <h4 className="font-semibold text-black mb-1 text-sm">{item.title}</h4>
                        <p className="text-xs text-darkGray/80">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Innovation Management quadrants */}
            <div className="space-y-5">
              <div className="text-center mb-2">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-2">
                  <Settings size={18} className="text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-black">Innovation Management</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  {
                    title: "Portfolio Governance",
                    icon: CheckCircle2,
                    description: "Structured decision-making and resource allocation processes"
                  },
                  {
                    title: "Implementation Support",
                    icon: Rocket, 
                    description: "Turn concepts into reality through systematic execution"
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-lightGray">
                    <div className="flex gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
                        {React.createElement(item.icon, { size: 16, className: 'text-primary' })}
                      </div>
                      <div>
                        <h4 className="font-semibold text-black mb-1 text-sm">{item.title}</h4>
                        <p className="text-xs text-darkGray/80">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Methodology benefits - Mobile optimized */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              {
                icon: Gauge,
                title: "Accelerated Results",
                description: "40% reduction in time-to-market for new initiatives"
              },
              {
                icon: CheckCircle2,
                title: "Higher Success Rate",
                description: "95% of projects achieve their business objectives"
              },
              {
                icon: Workflow,
                title: "Sustainable System",
                description: "Build lasting innovation capabilities within your organization"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-lightGray">
                <div className="w-9 h-9 rounded-full bg-primary/10 mb-2 flex items-center justify-center" aria-hidden="true">
                  {React.createElement(benefit.icon, { size: 16, className: 'text-primary' })}
                </div>
                <h3 className="text-sm font-semibold text-black mb-1">{benefit.title}</h3>
                <p className="text-xs text-darkGray/80">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* ISO Standards badge */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-lightGray">
            <Award size={16} className="text-primary" aria-hidden="true" />
            <span className="text-xs font-medium">Aligned with <span className="font-bold">ISO 56000</span> Innovation Management Standards</span>
          </div>
        </div>
      </div>
    </section>
  );
});

// --- Innovation Outcomes Section (Mobile optimized) ---
const InnovationOutcomesSection = memo(() => {
  return (
    <section className={`${SPACING.section} bg-white`} aria-labelledby="outcomes-heading">
      <div className={SPACING.container}>
        {/* Section header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-3">
            <BarChart size={18} className="text-primary" aria-hidden="true" />
          </div>
          <h2 id="outcomes-heading" className="text-2xl md:text-3xl font-bold text-black mb-3">
            Transforming Your Innovation Ecosystem
          </h2>
          <p className="text-base text-darkGray/80 max-w-2xl mx-auto">
            How our strategic innovation framework creates measurable business results across industries
          </p>
        </div>

        {/* Innovation outcomes - Stacked on mobile, 2-column on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Future Thinking Outcomes */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-lightGray">
            <div className="p-4 bg-gray-50 border-b border-lightGray">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <Compass size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black">Future Thinking Impact</h3>
                  <p className="text-xs text-darkGray/80">Fueling your innovation pipeline</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 md:p-5">
              <p className="text-darkGray/90 mb-4 text-sm">
                Our Future Thinking methodology helps organizations anticipate change and position themselves ahead of market disruption. By systematically scanning for emerging opportunities and threats, we help you build an innovation strategy that stays ahead of the curve.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {[
                  {
                    icon: Target,
                    title: "Opportunity Identification",
                    metric: "7-10",
                    description: "new opportunity spaces identified"
                  },
                  {
                    icon: Gauge,
                    title: "Risk Reduction",
                    metric: "30%",
                    description: "reduction in strategic risk exposure"
                  },
                  {
                    icon: GitBranch,
                    title: "R&D Targeting",
                    metric: "40%",
                    description: "higher ROI on development initiatives"
                  },
                  {
                    icon: Network,
                    title: "Ecosystem Positioning",
                    metric: "3-5",
                    description: "strategic partnership opportunities"
                  }
                ].map((outcome, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-lightGray/50">
                    <div className="flex items-start gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
                        {React.createElement(outcome.icon, { size: 14, className: 'text-primary' })}
                      </div>
                      <div className="mt-0.5">
                        <h4 className="font-semibold text-black text-xs">{outcome.title}</h4>
                      </div>
                    </div>
                    <div className="pl-9">
                      <div className="text-base font-bold text-primary">{outcome.metric}</div>
                      <p className="text-xs text-darkGray/80">{outcome.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg border border-lightGray/50">
                <div className="flex items-center gap-2 mb-1">
                  <Globe size={14} className="text-primary" aria-hidden="true" />
                  <h4 className="font-semibold text-black text-xs">Cross-Industry Application</h4>
                </div>
                <p className="text-xs text-darkGray/80">
                  Our Future Thinking approach has proven successful across manufacturing, technology, healthcare, financial services, energy, and consumer product industries.
                </p>
              </div>
            </div>
          </div>
          
          {/* Innovation Management Outcomes */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-lightGray">
            <div className="p-4 bg-gray-50 border-b border-lightGray">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <Settings size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black">Innovation Management Impact</h3>
                  <p className="text-xs text-darkGray/80">Optimizing your innovation process</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 md:p-5">
              <p className="text-darkGray/90 mb-4 text-sm">
                Our Innovation Management system transforms how organizations execute on opportunities. By implementing structured governance, evaluation frameworks, and implementation processes, we help you build an innovation architecture that consistently delivers measurable results.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {[
                  {
                    icon: Rocket,
                    title: "Accelerated Implementation",
                    metric: "40%",
                    description: "reduction in development cycles"
                  },
                  {
                    icon: CheckCircle2,
                    title: "Higher Success Rates",
                    metric: "95%",
                    description: "higher completion rate"
                  },
                  {
                    icon: BarChart,
                    title: "Resource Optimization",
                    metric: "60%",
                    description: "improvement in resource allocation"
                  },
                  {
                    icon: Workflow,
                    title: "Innovation Capability",
                    metric: "85%",
                    description: "embedded capability retention"
                  }
                ].map((outcome, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-lightGray/50">
                    <div className="flex items-start gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
                        {React.createElement(outcome.icon, { size: 14, className: 'text-primary' })}
                      </div>
                      <div className="mt-0.5">
                        <h4 className="font-semibold text-black text-xs">{outcome.title}</h4>
                      </div>
                    </div>
                    <div className="pl-9">
                      <div className="text-base font-bold text-primary">{outcome.metric}</div>
                      <p className="text-xs text-darkGray/80">{outcome.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg border border-lightGray/50">
                <div className="flex items-center gap-2 mb-1">
                  <Globe size={14} className="text-primary" aria-hidden="true" />
                  <h4 className="font-semibold text-black text-xs">Cross-Industry Application</h4>
                </div>
                <p className="text-xs text-darkGray/80">
                  Our Innovation Management system has transformed innovation performance in industries ranging from manufacturing and financial services to healthcare and technology.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Combined impact section - Mobile optimized */}
        <div className="bg-gray-50 rounded-xl shadow-sm p-5 md:p-6 border border-lightGray">
          <h3 className="text-lg font-bold text-black mb-5 text-center">The Combined Impact of a Complete Innovation Ecosystem</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              {
                metric: "3x",
                label: "Higher Innovation ROI",
                description: "Organizations implementing our complete innovation system see triple the return on innovation investments"
              },
              {
                metric: "76%",
                label: "New Revenue from Innovation",
                description: "On average, clients generate 76% more revenue from new products, services, and business models"
              },
              {
                metric: "85%",
                label: "Improved Market Position",
                description: "85% of clients report significant improvement in competitive market position within 24 months"
              }
            ].map((impact, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-4 text-center border border-lightGray/50">
                <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">
                  {impact.metric}
                </div>
                <h4 className="font-semibold text-black mb-1 text-sm">{impact.label}</h4>
                <p className="text-xs text-darkGray/80">{impact.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 flex justify-center">
            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-lightGray/50">
              <Award size={14} className="text-primary" aria-hidden="true" />
              <span className="text-xs">Based on data from 200+ client engagements across 12 industries</span>
            </div>
          </div>
        </div>
        
        {/* Industries section - Mobile optimized */}
        <div className="mt-10">
          <h3 className="text-base font-bold text-center mb-5">Industries Powered by Our Innovation Framework</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Box, label: "Manufacturing" },
              { icon: BarChart3, label: "Financial Services" },
              { icon: Users, label: "Healthcare" },
              { icon: Share2, label: "Technology" },
              { icon: Zap, label: "Energy" },
              { icon: Shield, label: "Insurance" },
              { icon: Rocket, label: "Aerospace" },
              { icon: BookOpen, label: "Education" }
            ].map((industry, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2 text-center border border-lightGray/50">
                <div className="mx-auto w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mb-1" aria-hidden="true">
                  {React.createElement(industry.icon, { size: 14, className: 'text-primary' })}
                </div>
                <span className="text-xs">{industry.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

// --- Services FAQ Section (Mobile optimized) ---
const ServicesFAQSection = memo(() => {
  const [openIndex, setOpenIndex] = useState(0);
  
  // Updated FAQs with better categorization and content
  const faqs = useMemo(() => [
    {
      question: "How do your services work together as an innovation ecosystem?",
      answer: "Our Future Thinking and Innovation Management services form a complete innovation ecosystem. Future Thinking feeds your innovation pipeline with high-potential opportunities identified before competitors, while Innovation Management provides the structure to efficiently evaluate, select, and implement these initiatives. Organizations can start with either service based on their most pressing needs, with the option to integrate both for a comprehensive innovation system."
    },
    {
      question: "What's the typical timeline for seeing results?",
      answer: "Our approach delivers value at multiple horizons: Short-term wins (2-3 months) through process improvements and quick implementation of ready ideas; Medium-term results (4-8 months) from new opportunity identification and capability building; and Long-term transformation (9+ months) through systematic innovation management and cultural change. We establish clear milestones and metrics to track progress throughout the journey."
    },
    {
      question: "What makes your innovation approach different?",
      answer: "Our strategic innovation framework stands out in three key ways: First, we integrate future-focused strategic insights with practical implementation, bridging the gap that causes many innovation initiatives to fail. Second, our methodology is highly adaptable to your specific industry and organizational context, rather than using a rigid one-size-fits-all approach. Third, we focus on building your internal innovation capabilities, ensuring your innovation ecosystem becomes self-sustaining rather than creating consultant dependency."
    },
    {
      question: "How do we measure the ROI of our innovation ecosystem?",
      answer: "Our innovation framework includes comprehensive measurement systems tailored to your specific objectives. We establish clear metrics in four categories: Input metrics (resources, capabilities, activities), Process metrics (efficiency, velocity, quality), Output metrics (new products, services, business models), and Outcome metrics (revenue, margin, market share). This balanced scorecard approach ensures you can track both immediate results and long-term value creation from your innovation investments."
    },
    {
      question: "What team members from our organization need to be involved?",
      answer: "Successful innovation initiatives typically involve a mix of leadership sponsorship, core team participation, and broader organizational engagement. We recommend: Executive sponsors to provide strategic direction and remove barriers; A dedicated cross-functional core team (2-5 people) to drive day-to-day activities; Subject matter experts who participate at specific points; and Broader stakeholder engagement for ideation and feedback. We design participation requirements to balance thoroughness with efficiency."
    },
    {
      question: "How do your services develop sustained innovation capabilities?",
      answer: "Building lasting innovation capabilities is central to our approach. Rather than creating dependency, we focus on knowledge transfer through collaborative methods, skill-building workshops, coaching, and documentation of processes and frameworks. We implement governance structures and measurement systems that reinforce innovation practices, and gradually transition from active implementation to advisory support as your team's capabilities mature."
    }
  ], []);

  return (
    <section id="faq" className={`${SPACING.section} bg-gradient-to-b from-white to-offWhite relative overflow-hidden`} aria-labelledby="faq-heading">
      {/* Simplified background elements */}
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-primaryLight rounded-full blur-3xl opacity-10" aria-hidden="true"></div>
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-secondaryLight rounded-full blur-3xl opacity-10" aria-hidden="true"></div>
      
      <div className={SPACING.container}>
        {/* Section header - Enhanced for SEO */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-block rounded-full bg-primaryLight px-3 py-1 mb-2">
            <h2 className="text-xs font-semibold text-primary uppercase tracking-wide">
              Frequently Asked Questions
            </h2>
          </div>
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-bold text-black mb-3">
            Common <span className="text-primary">Questions</span> About Our Services
          </h2>
          <p className="text-sm md:text-base text-darkGray max-w-2xl mx-auto">
            Learn more about how we deliver innovation success and build lasting capabilities.
          </p>
        </div>

        {/* FAQ Grid Layout - Mobile optimized */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`bg-white rounded-lg overflow-hidden shadow-sm border border-lightGray transition-all duration-300 ${
                openIndex === index ? 'ring-1 ring-primary/30' : 'hover:shadow-md'
              }`}
            >
              <button
                onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
                className="w-full flex justify-between items-start p-4 text-left"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h4 className="font-semibold text-black pr-6 text-sm">{faq.question}</h4>
                {openIndex === index ? (
                  <MinusCircle size={18} className="text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                ) : (
                  <PlusCircle size={18} className="text-darkGray flex-shrink-0 mt-0.5" aria-hidden="true" />
                )}
              </button>
              
              <div 
                id={`faq-answer-${index}`}
                className={`px-4 overflow-hidden transition-all duration-300 text-sm ${
                  openIndex === index ? 'max-h-96 pb-4' : 'max-h-0'
                }`}
                aria-hidden={openIndex !== index}
              >
                <p className="text-darkGray text-xs md:text-sm">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Enhanced FAQ metadata section for better SEO - Mobile optimized */}
        <div className="mt-10 max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-5 border border-lightGray">
          <div className="flex flex-col md:flex-row items-center gap-5">
            <div className="md:w-1/4 flex justify-center">
              <div className="w-14 h-14 rounded-full bg-primaryLight flex items-center justify-center" aria-hidden="true">
                <Lock size={20} className="text-primary" />
              </div>
            </div>
            <div className="md:w-3/4 text-center md:text-left">
              <h3 className="text-lg font-bold text-black mb-2">Additional Questions?</h3>
              <p className="text-xs md:text-sm text-darkGray mb-3">
                Our innovation experts are ready to address your specific questions about how our services can benefit your organization. We're committed to providing transparent information about our methodologies and results.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <div className="inline-flex items-center gap-1.5 bg-offWhite px-2 py-1 rounded-full text-xs">
                  <Award size={12} className="text-primary" aria-hidden="true" />
                  <span>ISO 56000 Compliant</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-offWhite px-2 py-1 rounded-full text-xs">
                  <Users size={12} className="text-secondary" aria-hidden="true" />
                  <span>200+ Successful Projects</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-offWhite px-2 py-1 rounded-full text-xs">
                  <CheckCircle2 size={12} className="text-primary" aria-hidden="true" />
                  <span>95% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// --- CTA Section (Mobile optimized) ---
const CTASection = memo(() => {
  return (
    <section id="contact" className="py-12 md:py-16 bg-gradient-to-r from-primary to-primaryDark text-white">
      <div className={SPACING.container}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Left content - Enhanced messaging */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
              <Star size={18} className="text-secondary" fill="currentColor" aria-hidden="true" />
              <h4 className="text-white/90 font-medium text-sm">Innovation Excellence</h4>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-center lg:text-left">
              Transform with Strategic Innovation
            </h2>
            
            <p className="text-white/90 text-sm md:text-base mb-6 max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
              Let's discuss how our innovation framework can help you build a sustainable ecosystem that delivers measurable business impact.
            </p>
            
            {/* Enhanced contact options - Mobile optimized */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button 
                href="/contact" 
                variant="secondary" 
                size="lg"
                icon={MessageSquare}
                className="w-full sm:w-auto justify-center shadow-md"
                aria-label="Schedule a consultation with our innovation experts"
              >
                Schedule a Consultation
              </Button>
              
              <Button 
                href="/assessment" 
                variant="white" 
                size="lg"
                icon={FileText}
                className="w-full sm:w-auto justify-center shadow-sm"
                aria-label="Get a free innovation assessment for your organization"
              >
                Get Innovation Assessment
              </Button>
            </div>
            
            {/* Trust indicators - Mobile optimized */}
            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-4 items-center justify-center lg:justify-start">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-secondary" aria-hidden="true" />
                <span className="text-white/80 text-xs">No-obligation consultation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-secondary" aria-hidden="true" />
                <span className="text-white/80 text-xs">Tailored to your industry</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-secondary" aria-hidden="true" />
                <span className="text-white/80 text-xs">Clear, measurable outcomes</span>
              </div>
            </div>
          </div>
          
          {/* Right content - Add a visual element - Hide on mobile */}
          <div className="lg:col-span-2 hidden lg:block">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 shadow-lg relative">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-xl"></div>
              <div className="relative">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Award size={18} className="text-secondary" aria-hidden="true" />
                  Why Choose Lusidea
                </h3>
                
                <ul className="space-y-2">
                  {[
                    "Proven methodology with 95% success rate",
                    "End-to-end innovation system",
                    "Industry-specific expertise",
                    "ISO 56000 aligned processes",
                    "Measurable business impact"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-white/20 flex-shrink-0 flex items-center justify-center mt-0.5 mr-2" aria-hidden="true">
                        <Check size={10} className="text-white" />
                      </div>
                      <span className="text-white/90 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default LusideaServicesPage;